import type { PortalAssetActor, PortalAssetHistoryEntry } from '../../types/portalAsset.js';
import { actorDisplayName, type ActorNameDirectory, type TileAudience } from './tile.js';

// AN ASSET'S DECISION HISTORY (2026-10-08). PURE — the server reads the asset's
// own `portalAsset` audit entries and hands them here.
//
// ⚠️⚠️ WHY THIS EXISTS: a club and Kyck are now EQUAL authorities on review
// (Scott, 2026-10-08) — either can approve, either can overturn the other, one
// shared status. Two parties flipping one field with no visible history is a
// silent tug-of-war, so each side must be able to see who decided what, when.
//
// ⚠️⚠️ THE AUDIT LOG IS THE RECORD; NOTHING IS ADDED TO THE ASSET DOC. A
// decision trail stored on the asset would be a second record that can disagree
// with the audit entry written in the same transaction as the change. This
// projects the one that already exists.
//
// ⚠️ Q3 still holds: `portalAsset` entries stay OFF the account History tab.
// This is the ASSET's page, not the account's.
//
// ⚠️ THE ACTOR is read from the entry's context suffix — `(staff)` /
// `(portalUser)`, written by every portal-asset audit call — plus its userId,
// which auditActorFor sets to the stored actor id on both sides (the uid for
// staff, the lowercased email for a portal user). An entry whose actor cannot be
// read is DROPPED rather than shown as "someone": a history that names the wrong
// person is worse than one with a gap.
//
// ⚠️ Edits (tags, category) are not decisions and are left out.

export type StoredAssetAudit = {
  id: string;
  /** ISO, already serialised. */
  timestamp: string | null;
  userId: string;
  context: string;
  after: unknown;
};

const KINDS: Array<{ prefix: string; kind: PortalAssetHistoryEntry['kind'] }> = [
  { prefix: 'portal-assets-upload ', kind: 'uploaded' },
  { prefix: 'portal-assets-review-approved ', kind: 'approved' },
  { prefix: 'portal-assets-review-rejected ', kind: 'rejected' },
  { prefix: 'portal-assets-delete ', kind: 'deleted' },
  { prefix: 'portal-assets-restore ', kind: 'restored' },
];

const SUFFIX = /\((staff|portalUser|system)\)$/;

function parse(e: StoredAssetAudit): { kind: PortalAssetHistoryEntry['kind']; actor: PortalAssetActor } | null {
  const match = KINDS.find((k) => e.context.startsWith(k.prefix));
  const suffix = SUFFIX.exec(e.context);
  if (!match || !suffix || typeof e.userId !== 'string' || e.userId.trim() === '') return null;
  return { kind: match.kind, actor: { kind: suffix[1] as PortalAssetActor['kind'], id: e.userId } };
}

/** The actors whose names must be resolved before projecting. */
export function historyActors(entries: readonly StoredAssetAudit[]): PortalAssetActor[] {
  return entries.map(parse).filter((p): p is NonNullable<typeof p> => p !== null).map((p) => p.actor);
}

function rejectionReason(after: unknown): string | null {
  const review = (after as { review?: { reason?: unknown } } | null)?.review;
  return typeof review?.reason === 'string' && review.reason.trim() !== '' ? review.reason : null;
}

/** Newest first, as the audit read returns them. */
export function toHistoryEntries(
  entries: readonly StoredAssetAudit[],
  audience: TileAudience,
  names: ActorNameDirectory,
): PortalAssetHistoryEntry[] {
  const out: PortalAssetHistoryEntry[] = [];
  for (const e of entries) {
    const p = parse(e);
    if (!p) continue;
    out.push({
      id: e.id,
      at: e.timestamp,
      kind: p.kind,
      byName: actorDisplayName(p.actor, audience, names),
      reason: p.kind === 'rejected' ? rejectionReason(e.after) : null,
    });
  }
  return out;
}

const VERB: Record<PortalAssetHistoryEntry['kind'], string> = {
  uploaded: 'Uploaded',
  approved: 'Approved',
  rejected: 'Not approved',
  deleted: 'Deleted',
  restored: 'Restored',
};

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "Not approved by Kyck on 8 Oct 2026 — Too dark". */
export function historySentence(e: PortalAssetHistoryEntry): string {
  const when = formatDate(e.at);
  const head = [VERB[e.kind], `by ${e.byName}`, when ? `on ${when}` : null].filter(Boolean).join(' ');
  return e.reason ? `${head} — ${e.reason}` : head;
}
